import * as React from 'react';
import { useImperativeHandle } from 'react';
import { Typography, ListItem, ListItemText, ListItemIcon, ListItemButton } from '@mui/material';
import { Box } from '@mui/material';
import VerticalHamburgerMenu from '../../common/controls/VerticalHamburgerMenu';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import { useHistoryAppointments } from '../../../hooks/appointments/useHistoryAppointments';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AppointmentView from '../modals/AppointmentView';
import { useDeleteAppointment } from '../../../hooks/appointments/useDeleteAppointment';
import { getAppointmentById } from '../../../api/appointmentService';
import { formatUtcToLocal } from '../../../utils/formatUtcToLocal';
import AppointmentForm from '../forms/AppointmentForm';

export default function AppointmentsListItems({ setListEmpty = null, setDoneLoading = null, history = null, historyEntry = null, pet = null, date = "", mode = 'history', upcoming = false, ref }) {
  const { t } = useTranslation('common');
  const { showSnackbar } = useSnackbar();

  const  {appointments, loading, loadMore, searchFunction, resetParent } = useHistoryAppointments(history?.id, historyEntry?.id, pet?.id, date, mode, upcoming);

  const [selectedAppointment, setSelectedAppointment] = React.useState(null);

  const [appointmentFormOpen, setAppointmentFormOpen] = React.useState(false);
  const [appointmentFormMode, setAppointmentFormMode] = React.useState('create');
  const [appointmentViewOpen, setAppointmentViewOpen] = React.useState(false);

  const onAppointmentSaveSuccess = async (updatedRecord) => {
    const updatedAppointment = await getAppointmentById(updatedRecord.id);
    setSelectedAppointment(updatedAppointment);
    resetParent();
  };

  const handleAppointmentCreate = () => {
    setSelectedAppointment(null);
    setAppointmentFormMode('create');
    setAppointmentFormOpen(true);
  };
  // Expose the function to parent
  useImperativeHandle(ref, () => ({
    handleAppointmentCreate
  }));

  const handleAppointmentEdit = (appointment) => {
    setSelectedAppointment(appointment);
    setAppointmentFormMode('edit');
    setAppointmentFormOpen(true);
  };
  const handleAppointmentDelete = async (appointment) => {
    try {
      await remove(appointment.id);
    } catch (err) {
      showSnackbar(t('appointments.messages.delete_fail'), "error");
    }
    
  };
  const onDeleteAppointmentSuccess = () => {
    resetParent();
    showSnackbar(t('appointments.messages.delete_success'), "success");
  };
  const { remove } = useDeleteAppointment(onDeleteAppointmentSuccess);

  const handleListItemClick = (appointment) => {
    setSelectedAppointment(appointment);
    setAppointmentViewOpen(true);
  };

  const handleListItemEdit = (vital) => {
    handleAppointmentEdit(vital);
  };

  const handleListItemDelete = (vital) => {
    handleAppointmentDelete(vital);
  };

  const statusToColor = (statusId) => {
    let color = "";
    switch (statusId) {
      case 1:
        color = "warning";
        break;
      case 2:
        color = "success";
        break;
      case 3:
      case 4:
      case 5:
        color = "error";
        break;
      case 6:
        color = "success";
        break;
      default:
        color = "grey";
    }
    return color;
  }

  React.useEffect(() => {
    if (appointments.data.length !== 0)
    {
      if (setListEmpty) setListEmpty(false);
    }
  }, [appointments]);

  React.useEffect(() => {
    if (!loading)
    {
      if (setDoneLoading) setDoneLoading(true);
    }
  }, [loading]);

  return (
    <>
      {loading ? (
        <></>
      ) : (
      appointments?.data.map((appointment) => (
        <ListItem 
            key={"appointment" + appointment.id}
            sx={{ py: 0 }}
            alignItems="flex-start"
            secondaryAction={
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <VerticalHamburgerMenu
                    options={[
                      { 
                        id: 1, 
                        name: 'View',
                        callback: () => handleListItemClick(appointment)
                      },
                      { 
                        id: 2, 
                        name: 'Edit',
                        callback: () => handleListItemEdit(appointment)
                      }
                    ].filter(option => !(option.name === 'Edit' && [3, 4, 5, 6].includes(appointment.status_id)))}
                  />
                </Box>

            }
        >
            <ListItemButton 
              sx={{ py: 0 }} 
              onClick={() => handleListItemClick(appointment)}
            >
            <ListItemIcon>
              <CalendarMonthIcon color='primary' />
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                { mode !== 'calendar' ?
                <Typography variant="body2" sx={{ maxWidth: '90%', m:0 }}>
                  {`${formatUtcToLocal(appointment.datetime, 'date')}`}
                </Typography>
                : null }
                <Typography variant="body2" sx={{ maxWidth: '90%', m:0 }}>
                  {`${formatUtcToLocal(appointment.datetime, 'time')}`}
                </Typography>
                {mode === 'dashboard' || mode === 'calendar' ? 
                <Typography variant="body2" sx={{ maxWidth: '90%', m:0 }}>
                  {`${appointment.pet.name}, (${appointment.petOwner.last_name}, ${appointment.petOwner.first_name})`}
                </Typography>
                : null }
                </>
              }
              secondary={
                <Typography color={statusToColor(appointment.status.id)} variant="body2" sx={{ my: 0, maxWidth: '90%' }}>
                  {`${appointment.status.name}`}
                </Typography>
              }
            />
            </ListItemButton>
        </ListItem>
      )))}
      <AppointmentForm
        mode={appointmentFormMode}
        appointment={selectedAppointment}
        successCallback={onAppointmentSaveSuccess}
        history={history}
        historyEntry={historyEntry}
        pet={pet} 
        open={appointmentFormOpen}
        onClose={() => setAppointmentFormOpen(false)}
      />
      <AppointmentView 
        handleEdit={handleAppointmentEdit}
        appointment={selectedAppointment}
        open={appointmentViewOpen}
        onClose={() => setAppointmentViewOpen(false)}
      />
    </>
  );
}